import React, { useState, useEffect } from 'react';
import { ShieldAlert, Globe, Activity, BarChart, Network, Shield, AlertTriangle, ChevronRight, Zap } from 'lucide-react';

function Dashboard() {
  // Real-time state management for various dashboard components
  const [pulseRate, setPulseRate] = useState(1000);
  const [globalThreatLevel, setGlobalThreatLevel] = useState(75);
  const [threatStatus, setThreatStatus] = useState('ELEVATED');
  
  // AI Chat response time state
  const [aiResponseTime, setAiResponseTime] = useState(124);
  
  // AI Chat last activity state
  const [aiLastActivity, setAiLastActivity] = useState(new Date());
  
  // Node result states
  const [nodeResults, setNodeResults] = useState([
    { id: 1, health: 'Healthy', performance: 98.7, status: 'Performance Optimal' },
    { id: 2, health: 'Healthy', performance: 99.1, status: 'Performance Optimal' },
    { id: 3, health: 'Healthy', performance: 97.4, status: 'Performance Optimal' }
  ]);
  
  // Indexer logs status
  const [indexerStats, setIndexerStats] = useState({
    logsProcessed: 1400000,
    indexingRate: 12700,
    latency: 124,
    uptime: 99.9
  });
  
  // Master node stats
  const [masterNodeStats, setMasterNodeStats] = useState({
    cpu: 12,
    memory: 3.8,
    load: 0.23
  });
  
  // Worker nodes stats
  const [workerNodes, setWorkerNodes] = useState([
    { id: 1, name: 'AbhiStrike-Worker-1', ip: 'Demo IP', status: 'Healthy', cpu: 23, memory: 4.2, tasks: 42 },
    { id: 2, name: 'AbhiStrike-Worker-2', ip: 'Demo IP', status: 'Healthy', cpu: 18, memory: 3.9, tasks: 38 },
    { id: 3, name: 'AbhiStrike-Worker-3', ip: 'Demo IP', status: 'Healthy', cpu: 36, memory: 3.1, tasks: 49 },
    { id: 4, name: 'AbhiStrike-Worker-4', ip: 'Demo IP', status: 'Healthy', cpu: 21, memory: 4.0, tasks: 41 },
    { id: 5, name: 'AbhiStrike-Worker-5', ip: 'Demo IP', status: 'Healthy', cpu: 19, memory: 3.7, tasks: 37 }
  ]);
  
  // AbhiEye stats
  const [abhiEyeStats, setAbhiEyeStats] = useState({
    threatIntel: 47832,
    darkWebNews: 12543,
    iocs: 1547
  });

  // Update real-time data at intervals
  useEffect(() => {
    // Update global threat level
    const threatInterval = setInterval(() => {
      const newThreatLevel = Math.floor(65 + Math.random() * 25);
      setGlobalThreatLevel(newThreatLevel);
      
      if (newThreatLevel > 85) {
        setThreatStatus('CRITICAL');
      } else if (newThreatLevel > 70) {
        setThreatStatus('ELEVATED');
      } else {
        setThreatStatus('MODERATE');
      }
    }, 5000);
    
    // Update node results
    const nodeInterval = setInterval(() => {
      setNodeResults(prev => prev.map(node => {
        const performanceChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 0.5);
        const newPerformance = Math.min(Math.max(node.performance + performanceChange, 95), 99.5);
        
        return {
          ...node,
          performance: parseFloat(newPerformance.toFixed(1)),
          status: newPerformance > 98 ? 'Performance Optimal' : 'Performance Good'
        };
      }));
    }, 3000);
    
    // Update indexer logs
    const indexerInterval = setInterval(() => {
      setIndexerStats(prev => ({
        logsProcessed: prev.logsProcessed + Math.floor(Math.random() * 10000),
        indexingRate: 12500 + Math.floor(Math.random() * 500),
        latency: 120 + Math.floor(Math.random() * 10),
        uptime: Math.min(99.99, prev.uptime + Math.random() * 0.01)
      }));
    }, 2000);
    
    // Update master node stats
    const masterInterval = setInterval(() => {
      setMasterNodeStats(prev => ({
        cpu: 10 + Math.floor(Math.random() * 5),
        memory: 3.5 + Math.random() * 0.5,
        load: 0.2 + Math.random() * 0.1
      }));
    }, 4000);
    
    // Update AI response time
    const aiInterval = setInterval(() => {
      setAiResponseTime(prev => {
        const change = (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 20);
        return Math.min(Math.max(prev + change, 80), 250);
      });
    }, 2000);
    
    // Update worker nodes
    const workersInterval = setInterval(() => {
      setWorkerNodes(prev => prev.map(worker => {
        let cpuChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 3);
        let memoryChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 0.2);
        let tasksChange = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 2);
        
        // Special handling for worker 3 which is under high load
        if (worker.id === 3) {
          cpuChange = Math.abs(cpuChange); // Always increase or stay the same
          if (worker.cpu > 85) {
            cpuChange = -Math.abs(cpuChange) / 2; // If too high, reduce slightly
          }
        }
        
        const newCpu = Math.min(Math.max(worker.cpu + cpuChange, 15), 90);
        const newMemory = Math.min(Math.max(worker.memory + memoryChange, 3.5), 8);
        const newTasks = Math.min(Math.max(Math.round(worker.tasks + tasksChange), 35), 95);
        
        let status = 'Healthy';
        if (newCpu > 70) status = 'High Load';
        
        return {
          ...worker,
          cpu: Math.round(newCpu),
          memory: parseFloat(newMemory.toFixed(1)),
          tasks: newTasks,
          status: status
        };
      }));
    }, 3500);
    
    // Simulating changing threat levels
    const pulseInterval = setInterval(() => {
      setPulseRate(prev => prev > 800 ? prev - 100 : 1000);
    }, 5000);
    
    // Update AbhiEye stats
    const abhiEyeInterval = setInterval(() => {
      setAbhiEyeStats(prev => ({
        threatIntel: prev.threatIntel + Math.floor(Math.random() * 10),
        darkWebNews: prev.darkWebNews + Math.floor(Math.random() * 5),
        iocs: prev.iocs + Math.floor(Math.random() * 15) + 5
      }));
    }, 4000);
    
    // Clean up all intervals on component unmount
    return () => {
      clearInterval(threatInterval);
      clearInterval(nodeInterval);
      clearInterval(indexerInterval);
      clearInterval(masterInterval);
      clearInterval(workersInterval);
      clearInterval(pulseInterval);
      clearInterval(aiInterval);
      clearInterval(abhiEyeInterval);
    };
  }, []);

  // Function to format time difference
  const formatTimeDifference = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    
    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);
    const seconds = diffSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ago`;
  };

  // Random data for visualization
  const generateData = () => {
    return Array.from({ length: 48 }).map(() => Math.floor(15 + Math.random() * 85));
  };

  const [activityData] = useState(generateData());

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8 relative">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-teal-900/30 rounded-md bg-slate-800/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-teal-400 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SYS:</span> ACTIVE
          </div>
          <div className="text-teal-400 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">PROT:</span> MAXIMAL
          </div>
          <div className="text-teal-400 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">SEC:</span> LEVEL 4
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-teal-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="mb-8 sm:mb-12 relative">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <div className="relative">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1">
                AbhiStrike <span className="text-teal-400">SIEM & XDR Solution</span>
              </h1>
              <div className="absolute -top-2 -right-2 w-2 h-2 bg-teal-500 rounded-full animate-ping"></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-teal-400 text-xs sm:text-base mb-4 sm:mb-6">
              <ShieldAlert className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Real-time Security</span>
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 hidden sm:block"></span>
              <span className="hidden sm:inline">Advanced Threat Detection</span>
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 hidden sm:block"></span>
              <span className="hidden sm:inline">AI-Powered Response</span>
            </div>
          </div>
          
          <div className="bg-slate-800/40 rounded-md border border-teal-900/30 p-3 sm:p-4 backdrop-blur-sm w-full sm:w-auto">
            <div className="flex items-center space-x-4">
              <div>
                <div className="text-xs sm:text-sm text-slate-400 mb-1">Global Threat Level</div>
                <div className="text-xl sm:text-2xl font-bold text-teal-400">{threatStatus}</div>
              </div>
              <div className="relative h-14 w-14 sm:h-16 sm:w-16">
                <svg className="w-full h-full" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="16" fill="none" stroke="#27272a" strokeWidth="2"></circle>
                  <circle
                    cx="18" cy="18" r="16"
                    fill="none" stroke="#14b8a6" strokeWidth="2"
                    strokeDasharray="100"
                    strokeDashoffset={100 - globalThreatLevel}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)">
                  </circle>
                </svg>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg sm:text-xl font-bold">{globalThreatLevel}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 1. AbhiStrike Regional Portals */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-teal-500 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-teal-800/50 after:to-transparent after:ml-3">
        <Globe className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-teal-500" />
        AbhiStrike Portals
      </h2>
      
      <div className="mb-8 sm:mb-10">
        {/* Pakistan Portal - Enhanced Card */}
        <div 
          className="group relative overflow-hidden rounded-xl border border-slate-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-teal-600/50 hover:shadow-[0_0_40px_rgba(20,184,166,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/10 to-cyan-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-slate-900/70 rounded-lg border border-teal-900/30 group-hover:border-teal-600/60 transition-colors">
                  <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/sidebar.png" alt="Abhi" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AbhiStrike</h3>
                  <p className="text-teal-400 text-xs sm:text-sm">Non-Production Environment</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-emerald-900/30 text-emerald-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-emerald-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Operational Status</span>
                <span className="text-emerald-400 text-xs sm:text-sm font-mono">OPTIMAL</span>
              </div>
              <div className="w-full h-2 bg-slate-700/70 rounded-full overflow-hidden">
                <div className="h-full w-[98%] bg-gradient-to-r from-teal-800 via-teal-500 to-teal-600 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-teal-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6 bg-slate-900/40 p-3 sm:p-4 rounded-lg border border-slate-700/80">
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                AbhiStrike SIEM & XDR provide comprehensive threat detection, incident response, and continuous monitoring for KSA, Oman, UAE and Pakistan digital and on-prem infrastructure through advanced AbhiStrike SIEM & XDR technology.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Globe className="h-3 w-3 mr-1 text-teal-400" />
                  Regions Monitoring
                </p>
                <p className="text-base sm:text-xl font-bold">4</p>
                <p className="text-xs text-slate-500">KSA, Oman, UAE, Pakistan</p>
              </div>
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Network className="h-3 w-3 mr-1 text-cyan-400" />
                  Assets Capacity
                </p>
                <p className="text-base sm:text-xl font-bold">100</p>
              </div>
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Shield className="h-3 w-3 mr-1 text-teal-400" />
                  Coverage
                </p>
                <p className="text-base sm:text-xl font-bold">100<span className="text-xs sm:text-sm font-normal">%</span></p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-teal-400" />
                <span className="text-xs sm:text-sm text-teal-400 font-mono">Last: 00:03:21 ago</span>
              </div>
              <a 
                href="https://abhistrike-v2.abhitech.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-teal-900/40 hover:bg-teal-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-teal-600/40 group-hover:border-teal-500/70 group-hover:shadow-[0_0_15px_rgba(20,184,166,0.3)]"
              >
                <span>Access Portal</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 2. AbhiStrike AI Chat System */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-teal-500 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-teal-800/50 after:to-transparent after:ml-3">
        <Activity className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-teal-500" />
        AbhiStrike AI Chat System
      </h2>
      
      <div className="mb-8 sm:mb-10">
        <div 
          href="https://demo-pk.abhistrike.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="group relative overflow-hidden rounded-xl border border-slate-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-teal-600/50 hover:shadow-[0_0_40px_rgba(20,184,166,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/10 to-cyan-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-slate-900/70 rounded-lg border border-teal-900/30 group-hover:border-teal-600/60 transition-colors">
                  <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AbhiStrike AI Chat</h3>
                  <p className="text-teal-400 text-xs sm:text-sm">Advanced AI-Powered AlertStrike Chat System</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-emerald-900/30 text-emerald-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-emerald-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">AI System Status</span>
                <span className="text-emerald-400 text-xs sm:text-sm font-mono">OPERATIONAL</span>
              </div>
              <div className="w-full h-2 bg-slate-700/70 rounded-full overflow-hidden">
                <div className="h-full w-[97%] bg-gradient-to-r from-teal-800 via-teal-500 to-teal-600 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-teal-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6 bg-slate-900/40 p-3 sm:p-4 rounded-lg border border-slate-700/80">
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                Advanced AI-Powered AlertStrike Chat System platform that enables interactive conversations and real-time understanding of cyber security situations through natural human language. It goes beyond simply analyzing logs - AbhiStrike interprets them intelligently and recommends the most effective cybersecurity actions based on the context of each alerts. You can select your environment to ensure compliance with local data regulations while using the platform.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Activity className="h-3 w-3 mr-1 text-teal-400" />
                  AI Model
                </p>
                <p className="text-base sm:text-xl font-bold">Abhi-G1</p>
              </div>
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Zap className="h-3 w-3 mr-1 text-cyan-400" />
                  Response
                </p>
                <p className="text-base sm:text-xl font-bold">{aiResponseTime}ms</p>
              </div>
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Activity className="h-3 w-3 mr-1 text-teal-400" />
                  Status
                </p>
                <p className="text-base sm:text-xl font-bold">Online</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-teal-400" />
                <span className="text-xs sm:text-sm text-teal-400 font-mono">Last: {formatTimeDifference(aiLastActivity)}</span>
              </div>
              <a 
                href="http://119.163.166.71:8080" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-teal-900/40 hover:bg-teal-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-teal-600/40 group-hover:border-teal-500/70 group-hover:shadow-[0_0_15px_rgba(20,184,166,0.3)]"
              >
                <span>Launch Chat System</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 3. AbhiEye Dark Web Monitoring */}
      <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-teal-500 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-teal-800/50 after:to-transparent after:ml-3">
        <ShieldAlert className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-teal-500" />
        AbhiEye Dark Web Monitoring
      </h2>
      
      <div className="mb-8 sm:mb-10">
        <div 
          className="group relative overflow-hidden rounded-xl border border-slate-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-teal-600/50 hover:shadow-[0_0_40px_rgba(20,184,166,0.2)] hover:-translate-y-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/10 to-cyan-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          {/* Animated Corner Accent */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-10 h-1.5 bg-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="absolute top-0 right-0 h-10 w-1.5 bg-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          
          <div className="relative p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 sm:p-3 bg-slate-900/70 rounded-lg border border-teal-900/30 group-hover:border-teal-600/60 transition-colors">
                  <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/abhieye-new-logo.png" alt="AbhiEye" className="h-6 w-6 sm:h-8 sm:w-8" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">AbhiEye AI Intel</h3>
                  <p className="text-teal-400 text-xs sm:text-sm">Dark Web Intelligence & Monitoring</p>
                </div>
              </div>
              <div className="relative">
                <span className="bg-emerald-900/30 text-emerald-400 text-xs px-2 sm:px-3 py-1 rounded-full border border-emerald-900/30 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                  <span>Active</span>
                </span>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-xs sm:text-sm">Intelligence System Status</span>
                <span className="text-emerald-400 text-xs sm:text-sm font-mono">OPERATIONAL</span>
              </div>
              <div className="w-full h-2 bg-slate-700/70 rounded-full overflow-hidden">
                <div className="h-full w-[96%] bg-gradient-to-r from-teal-800 via-teal-500 to-teal-600 rounded-full relative">
                  <div className="absolute top-0 left-0 h-full w-20 bg-gradient-to-r from-transparent via-teal-300/30 to-transparent animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6 bg-slate-900/40 p-3 sm:p-4 rounded-lg border border-slate-700/80">
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                Intelligence-based monitoring and dark web monitoring platform that focuses on accelerating daily workflows, including new aggregation and analysis, IOC and email investigations, and the creation of threat detection rules.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <ShieldAlert className="h-3 w-3 mr-1 text-teal-400" />
                  Threat Intel
                </p>
                <p className="text-base sm:text-xl font-bold">{abhiEyeStats.threatIntel.toLocaleString()}</p>
              </div>
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Zap className="h-3 w-3 mr-1 text-cyan-400" />
                  Analysis
                </p>
                <p className="text-base sm:text-xl font-bold">{abhiEyeStats.darkWebNews.toLocaleString()}</p>
                <p className="text-xs text-slate-500">Dark Web News</p>
              </div>
              <div className="bg-slate-900/40 p-2 sm:p-3 rounded-lg border border-slate-700/80 group-hover:border-teal-900/30 transition-colors">
                <p className="text-slate-400 text-xs mb-1 flex items-center">
                  <Activity className="h-3 w-3 mr-1 text-teal-400" />
                  Detection
                </p>
                <p className="text-base sm:text-xl font-bold">{(abhiEyeStats.iocs / 1000).toFixed(1)}K</p>
                <p className="text-xs text-slate-500">IOCs</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-teal-400" />
                <span className="text-xs sm:text-sm text-teal-400 font-mono">Last: 00:01:47 ago</span>
              </div>
              <a 
                href="http://abhieye-v2.abhitech.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-teal-900/40 hover:bg-teal-800/60 backdrop-blur transition-all px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm border border-teal-600/40 group-hover:border-teal-500/70 group-hover:shadow-[0_0_15px_rgba(20,184,166,0.3)]"
              >
                <span>Access AbhiEye</span>
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 transform group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>

    </main>
  );
}

export default Dashboard;
